import { Box, Stack, Typography } from "@mui/material";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import type { Field } from "../../api/types/objectTypes";
import FieldIcon from "../common/domain-icons/FieldIcon";
import AutoFilledMarker from "./AutoFilledMarker";
import FieldConfidenceWarning from "./FieldConfidenceWarning";
import FieldValueComponent from "./FieldValueComponent";
import { useFieldValuesContext } from "./FieldValuesContext";
import { isFieldConfidenceLow } from "./helpers";
import ObjectFieldCitationButton from "./ObjectFieldCitationButton";

interface Props {
  field: Field;
  fieldValue: unknown;
  fieldInputId?: string;
}

const FieldComponent = ({ field, fieldValue, fieldInputId }: Props) => {
  const { fieldsConfidence, fieldsCitations, onFieldValueChange, fieldsAutoFillTooltips } = useFieldValuesContext();

  const citation = fieldsCitations?.[field.id];
  const autoFillTooltip = fieldsAutoFillTooltips?.[field.id];

  return (
    <Stack direction="row" spacing={1.5} sx={{ width: "100%", alignItems: "flex-start" }}>
      <Box sx={{ pt: 1.25 }}>
        <FieldIcon fieldType={field.type} color="action" />
      </Box>
      <Box sx={{ width: 160, mr: 1.5, pt: 1.25 }}>
        <Typography color="textSecondary">
          {field.name}
          {field.attributes.includes("Required") && <RequiredFieldMarker />}
        </Typography>
      </Box>
      <Box sx={{ px: 1, width: "100%" }}>
        <FieldValueComponent
          field={field}
          fieldValue={fieldValue}
          onFieldValueChange={onFieldValueChange}
          fieldInputId={fieldInputId}
        />
      </Box>
      {isFieldConfidenceLow(fieldsConfidence?.[field.id]) && (
        <Box sx={{ pt: 1.25 }}>
          <FieldConfidenceWarning />
        </Box>
      )}
      {autoFillTooltip && (
        <Box sx={{ pt: 1.25 }}>
          <AutoFilledMarker tooltip={autoFillTooltip} />
        </Box>
      )}
      {citation && <ObjectFieldCitationButton citationKey={field.id} />}
    </Stack>
  );
};

export default FieldComponent;
